"use client";
const Button = dynamic(() => import("@/Components/Button"), { ssr: true });
// import ButtonLink from "@/Components/ButtonLink";
import React, { useEffect, useState } from "react";
const CustomCheckbox = dynamic(() => import("../../Home/Quote/QuoteForm/CustomCheckbox"), { ssr: true });
import Image from "next/image";
import dynamic from "next/dynamic";

type Category = "industries" | "projectTypes" | "technologies";

const categoryDisplayNames: Record<Category, string> = {
  industries: "Industries",
  projectTypes: "Project Types",
  technologies: "Technologies",
};

interface Option {
  name: string;
  slug: string;
}
interface FiltersProps {
  isChanged: boolean;
  selectedFilters: any;
  variant?: "blue" | "green";
  availableFilters: Record<Category, Option[]>;
  onApply: (applied: Record<string, string[]>) => void;
  onClose?: () => void;
}

const Filters: React.FC<FiltersProps> = ({ onApply,
  onClose,
  isChanged,
  variant = "blue",
  availableFilters,
  selectedFilters,
}) => {
  const [openId, setOpenId] = useState("");
  const [searchQuery, setSearchQuery] = useState<Record<string, string>>({});
  const [selectedDraft, setSelectedDraft] = useState<Record<string, string[]>>(
    {}
  );
  const [appliedFilters, setAppliedFilters] = useState<
    Record<string, string[]>
  >({});
  useEffect(() => {
    const selected = {
      industries: selectedFilters.industries.map((item: any) => item.slug),
      projectTypes: selectedFilters.projectTypes.map((item: any) => item.slug),
      technologies: selectedFilters.technologies.map((item: any) => item.slug),
    };
    setSelectedDraft(selected);
    setAppliedFilters(selected);
  }, [isChanged, selectedFilters]);

  const toggleOption = (filterId: string, option: string) => {
    setSelectedDraft((prev) => {
      const prevValues = prev[filterId] || [];
      return {
        ...prev,
        [filterId]: prevValues.includes(option)
          ? prevValues.filter((v) => v !== option)
          : [...prevValues, option],
      };
    });
  };

  const handleApply = () => {
    setAppliedFilters(selectedDraft);
    onApply(selectedDraft); // send data back to parent
  };

  const handleClear = () => {
    setSelectedDraft({});
    setAppliedFilters({});
    const post = { industries: [], projectTypes: [], technologies: [] };
    onApply(post);
  };
  const removeChip = (filterId: string, value: string) => {
    setAppliedFilters((prev) => ({
      ...prev,
      [filterId]: prev[filterId].filter((v) => v !== value),
    }));

    setSelectedDraft((prev) => ({
      ...prev,
      [filterId]: (prev[filterId] || []).filter((v) => v !== value),
    }));
  };

  const strokeColor = variant === "blue" ? "#0086E6" : "#16A34A";

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex justify-between items-center py-5 px-6 border-b">
        <h3 className="text-subtitle-2 font-semibold text-primary-text">
          Filter by
        </h3>
        {onClose && (
          <button
            onClick={onClose}
            className="aspect-square w-[32px] flex justify-center items-center border bg-blue-50 border-border cursor-pointer"
            aria-label="Close filter"
          >
            <Image src="/images/close.svg" alt="close" width={20} height={20} />
          </button>
        )}
      </div>

      {/* Accordion */}
      <div className="p-4 space-y-3 overflow-y-auto flex-1">
        {/* Chips */}
        <div className="flex flex-wrap gap-3">
          {Object.entries(appliedFilters).flatMap(([key, values]) =>
            values.map((v) => (
              <span
                key={`${key}-${v}`}
                className="px-4 py-2 bg-blue-500 text-background text-subtitle-4 flex items-center gap-2.5"
              >
                {v}
                <button
                  onClick={() => removeChip(key, v)}
                  className="ml-1 text-white cursor-pointer"
                >
                  ✕
                </button>
              </span>
            ))
          )}
        </div>
        <div className="border border-border">
          {(Object.keys(availableFilters) as Category[])?.map((f, i) => (
            <div key={i} className="p-3 border border-border">
              <button
                className="w-full flex justify-between items-center py-2 font-medium cursor-pointer"
                onClick={() => setOpenId(openId === f ? "" : f)}
              >
                {categoryDisplayNames[f]}
                <figure
                  className={`aspect-square w-[32px] flex justify-center items-center border ${variant === "blue"
                    ? "bg-blue-50 border-border "
                    : "bg-green-50 border-green-100"
                    }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M13.2788 5.9668L8.93208 10.3135C8.41875 10.8268 7.57875 10.8268 7.06542 10.3135L2.71875 5.9668"
                      stroke={strokeColor}
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </figure>
              </button>

              {openId === f && (
                <div>
                  <div className="p-3 border border-border flex items-center gap-2">
                    <Image
                      src="/images/SearchIcon.svg"
                      alt="search"
                      width={20}
                      height={20}
                    />
                    <input
                      type="search"
                      placeholder={`Search ${categoryDisplayNames[f]}...`}
                      className="w-full text-sm focus-within:outline-none"
                      value={searchQuery[f] || ""}
                      onChange={(e) =>
                        setSearchQuery((prev) => ({
                          ...prev,
                          [f]: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="space-y-1 mt-2.5">
                    {availableFilters[f]
                      .filter((o) =>
                        o.name
                          .toLowerCase()
                          .includes((searchQuery[f] || "").toLowerCase())
                      )
                      .map((o, i) => (
                        <CustomCheckbox
                          key={i}
                          name={o.name}
                          onChange={() => toggleOption(f, o.name)}
                          checked={(selectedDraft[f] || []).includes(o.name)}
                          label={o.name}
                          labelClass="text-subtitle-2 font-semibold text-primary-text mt-[5px] align-middle"
                          wrapperClass="flex justify-start gap-3 items-center cursor-pointer hover:bg-gray-100 p-3 w-full"
                        />
                      ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Apply button */}
      <div className="p-4">
        <Button
          label="Apply Filters"
          variant="small"
          onClick={handleApply}
          className="w-full"
        />

        <button
          onClick={handleClear}
          className="px-5 xl:pt-[12px] xl:pb-[10px] pt-[11px] pb-[9px] text-subtitle-3 text-primary-text w-full cursor-pointer flex items-center justify-center gap-2.5 font-semibold transition-all duration-300 group"
        >
          Clear selection
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="my-[3px]"
          >
            <path
              d="M1.25 9.6582H17.75"
              stroke="#32343B"
              strokeWidth="1.5"
              strokeLinecap="square"
              strokeLinejoin="round"
              className="lg:group-hover:opacity-100 xl:opacity-0 opacity-100 transition-all duration-300"
            />
            <path
              d="M11.5908 2.5L18.7499 9.65909L11.5908 16.8181"
              stroke="#32343B"
              strokeWidth="1.5"
              strokeLinecap="square"
              strokeLinejoin="round"
              className="lg:group-hover:translate-x-0 xl:-translate-x-[3px] -translate-x-0 transition-all duration-300"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};
export default Filters;
